import React from 'react';
import { Label } from '../../../../../components/Form/Label/Label';
import { t } from '../../../../../locales/translate';
import { graphql } from '../../../../../generated';
import { FormForm } from '../../../../../components/Form/FormForm';
import { FormCheckbox } from '../../../../../components/Form/Checkbox/FormCheckbox';
import { useMutationHandler } from '../../../../../model/graphql';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { FormRadioGroup } from '../../../../../components/Form/Radio/FormRadioGroup';
import { Stack } from '../../../../../components/Stack/Stack';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

interface Props {
  productId: string;
  onClose: () => void;
  resaleSetting: ResaleSetting;
}

export type ResaleSetting = {
  preference: 'OFF' | 'ON_SOLD_OUT' | 'ALLOW';
  visibility: Array<'PUBLIC_OFFER' | 'PRIVATE_LINK'>;
};

const CHANGE_RESALE_SETTING = graphql(`
  mutation ChangeResaleSetting($id: ID!, $preference: String!, $visibility: [String!]!) {
    changeResaleSetting(input: { eventId: $id, preference: $preference, visibility: $visibility }) {
      resaleSetting {
        preference
        visibility
      }
    }
  }
`);

export function ResaleSettingForm({ productId, onClose, resaleSetting }: Props): JSX.Element {
  const [changeResaleSetting, { loading }] = useMutationHandler(CHANGE_RESALE_SETTING);
  const { model, fields, onSubmit, valid, dirty } = useForm({
    model: resaleSetting,
    validations: {
      visibility: ({ model }) => {
        if (model.visibility.length === 0) {
          return [t.model.event.resale.visibilitySettingsRequired];
        }
        return [];
      },
    },
    onSubmit: async () => {
      await changeResaleSetting({
        variables: {
          id: productId,
          preference: model.preference,
          visibility: model.visibility,
        },
      });
      onClose();
    },
  });

  function handleVisibilityChange(key: 'PUBLIC_OFFER' | 'PRIVATE_LINK', value: boolean): void {
    let updatedVisibility = [...model.visibility];
    if (value === true && !updatedVisibility.includes(key)) {
      updatedVisibility.push(key);
    } else {
      updatedVisibility = updatedVisibility.filter((v) => v !== key);
    }
    fields.visibility.touch();
    fields.visibility.onChange(updatedVisibility);
  }

  return (
    <FormForm
      canSubmit={valid && dirty}
      onSubmit={onSubmit}
      onClose={onClose}
      loading={loading}
      saveButtonTitle={t.model.event.resale.confirm}
    >
      <Stack>
        <Label title={t.model.event.resale.resalePreference} />
        <FormRadioGroup<ResaleSetting['preference']>
          {...fields.preference}
          name="preference"
          options={[
            { label: t.model.event.resale.allow, value: 'ALLOW' },
            { label: t.model.event.resale.onSoldOut, value: 'ON_SOLD_OUT' },
            { label: t.model.event.resale.off, value: 'OFF' },
          ]}
          layout="list"
        />
        <Collapse open={fields.preference.value !== 'OFF'}>
          <Stack>
            <Label title={t.model.event.resale.visibilitySettings} />
            <FormCheckbox
              {...fields.visibility}
              label={t.model.event.resale.publicOffer}
              value={fields.visibility.value.includes('PUBLIC_OFFER')}
              onChange={(value) => handleVisibilityChange('PUBLIC_OFFER', !!value)}
            />
            <FormCheckbox
              {...fields.visibility}
              label={t.model.event.resale.privateLink}
              value={fields.visibility.value.includes('PRIVATE_LINK')}
              onChange={(value) => handleVisibilityChange('PRIVATE_LINK', !!value)}
            />
          </Stack>
        </Collapse>
      </Stack>
    </FormForm>
  );
}
